import { useMemo } from 'react';
import { Decimal } from 'decimal.js';
import {
  useListRiskSummaryFull
} from '@/lib/api/risk/summary';
import '@/styles/riskCompactTable.css';
import { DataTable, type DataTableColumn } from "@/components/ui/DataTable/index";
import { formatNumberToDisplay } from '@/lib/api/common';
import { Commodities, CommodityOptions } from '@/lib/api/commodity';
import { Skeleton } from '@/components/ui/skeleton';
import { convert_to_groupable_commodity_type, get_groupable_commodity_display_name } from './common';


interface TopLevelStatsRow {
  id: string;
  commodity: string;
  acreage: number;
  projected_production: number;
  actual_production: number;
}

export function TopLevelSummaryTable({ baseTopLevelResult }: { baseTopLevelResult: ReturnType<typeof useListRiskSummaryFull> }) {
  const summaryData = baseTopLevelResult?.data?.data?.summary || {};

  const { top_level_rows, top_level_columns, isReady } = useMemo(() => {
    if (!summaryData?.commodity_list) return { top_level_rows: [], top_level_columns: [], isReady: false };

    const topLevel = summaryData.commodity_list?.reduce((acc: Record<string, { acreage: Decimal, projected_production: Decimal, actual_production: Decimal }>, base_commodity_type: string) => {
      const commodity_type = convert_to_groupable_commodity_type(base_commodity_type as Commodities);
      const commodity_display_name = get_groupable_commodity_display_name(commodity_type);

      if (!acc[commodity_type]) {
        acc[commodity_type] = { acreage: new Decimal(0), projected_production: new Decimal(0), actual_production: new Decimal(0) };
      }

      const base_stats = summaryData.customer_commodity_stats[commodity_type];
      if (base_stats) {
        for (const pair of Object.entries(base_stats)) {
          const [customer_id, customer_stats] = pair as any;

          acc[commodity_type].acreage = acc[commodity_type].acreage.plus(customer_stats.acreage || 0);
          acc[commodity_type].projected_production = acc[commodity_type].projected_production.plus(customer_stats.projected_production || 0);
          acc[commodity_type].actual_production = acc[commodity_type].actual_production.plus(customer_stats.actual_production || 0);
        }
      }

      return acc;
    }, {});

    const top_level_columns: DataTableColumn<TopLevelStatsRow>[] = [
      {
        id: "commodity",
        accessorKey: "commodity",
        header: "Commodity",
        cell: ({ getValue }) => getValue() as string,
      },
      {
        id: "acreage",
        accessorKey: "acreage",
        header: "Acreage",
        filterFn: "numberFilter" as any,
        cell: ({ getValue }) => {
          return (
            <span className="text-gray-600 text-sm">
              {formatNumberToDisplay(getValue() as number)}
            </span>
          )
        },
      },
      {
        id: "projected_production",
        accessorKey: "projected_production",
        header: "Projected Production",
        filterFn: "numberFilter" as any,
        cell: ({ getValue }) => {
          return (
            <span className="text-gray-600 text-sm">
              {formatNumberToDisplay(getValue() as number)}
            </span>
          )
        },
      },
      {
        id: "actual_production",
        accessorKey: "actual_production",
        header: "Actual Production",
        filterFn: "numberFilter" as any,
        cell: ({ getValue }) => {
          return (
            <span className="text-gray-600 text-sm">
              {formatNumberToDisplay(getValue() as number)}
            </span>
          )
        },
      },
    ];

    return {
      top_level_rows: Object.entries(topLevel).map(([commodity_type, stats]: any) => ({
        id: commodity_type,
        commodity: CommodityOptions.find(option => option.value === commodity_type)?.label || commodity_type,
        acreage: stats.acreage.toNumber(),
        projected_production: stats.projected_production.toNumber(),
        actual_production: stats.actual_production.toNumber(),
      })).sort((a, b) => a.id.localeCompare(b.id)),
      top_level_columns,
      isReady: true
    };
  }, [summaryData]);

  return (
    <div className="gap-4 max-w-[calc(100vw-350px)] ">
      <h2 className="text-lg font-bold">General Overview</h2>
        <DataTable
          className="mx-4"
          tableClassName={top_level_rows?.length > 4 ? "max-h-[380px] overflow-y-auto" : "max-h-[270px] overflow-y-auto"}
          columns={top_level_columns}
          data={top_level_rows}
          stickyHeaders
          enableSorting
          enableMultiSort
          enableFiltering
          enableGrouping
          enableColumnResizing
          enableExport
          enableVirtualization
          enableColumnPinning
          virtualizationRowHeight={49}
          virtualizationOverscan={20}
          filterStyle='select'
          filterMode='internal'
          emptyMessage={!isReady ? "Loading Data" : "No data available"}
          defaultSorting={[
            { id: "commodity", desc: false }
          ]}
      />
    </div>
  )
}
